import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  DiagramToCodePlugin,
  exportToBlob,
  getTextFromElements,
  MIME_TYPES,
  TTDDialog,
} from "@excalidraw/excalidraw";
import { getDataURL } from "@excalidraw/excalidraw/data/blob";
import { safelyParseJSON } from "@excalidraw/common";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";
import type { TTDPersistenceAdapter } from "@excalidraw/excalidraw";
import { trackAIUsage } from "../../packages/excalidraw/aiUsageTracker";

const ttdPersistenceAdapter: TTDPersistenceAdapter = {
  loadChats: async () => {
    const data = safelyParseJSON(localStorage.getItem("excalidraw-ttd-chats") || "[]");
    return (Array.isArray(data) ? data : []) as any;
  },
  saveChats: async (chats: any) => localStorage.setItem("excalidraw-ttd-chats", JSON.stringify(chats))
};

const AIToolbox = ({ excalidrawAPI }: { excalidrawAPI: ExcalidrawImperativeAPI }) => {
  const [modalContent, setModalContent] = useState<{ title: string; content: string; language: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [toolbarNode, setToolbarNode] = useState<HTMLElement | null>(null);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsLoading(true);
    setIsDropdownOpen(false);
    
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const dataURL = event.target?.result as string;
          const base64Data = dataURL.split(",")[1];
          const mimeType = dataURL.split(",")[0].split(":")[1].split(";")[0];
          
          const model = await getGeminiModel();
          const prompt = `Analyze this hand-drawn sketch, image, or wireframe. Generate a detailed Mermaid JS flowchart, mindmap, or diagram that accurately represents the contents and structure of the image. Output ONLY valid Mermaid syntax inside \`\`\`mermaid ... \`\`\` block. Do not use custom styling classes that aren't natively supported. Use standard nodes and edges. CRITICAL: You MUST ALWAYS start your output with a valid diagram type declaration (e.g., \`flowchart TD\`, \`graph LR\`). NEVER start directly with a \`subgraph\`.`;
          
          const imagePart = { inlineData: { data: base64Data, mimeType } };
          const result = await model.generateContent([prompt, imagePart]);
          const responseText = await result.response.text();
          const tokensUsed = result.response.usageMetadata?.totalTokenCount || 0;
          trackAIUsage(tokensUsed);
          
          let cleanText = responseText;
          const match = responseText.match(/```(?:mermaid)?([\s\S]*?)```/);
          if (match) {
            cleanText = match[1].trim();
          } else if (responseText.startsWith("```") && responseText.endsWith("```")) {
            cleanText = responseText.substring(3, responseText.length - 3).trim();
          }

          setModalContent({
            title: "Image to Diagram (Mermaid)",
            content: cleanText,
            language: "mermaid"
          });
        } catch (err: any) {
          alert("Error generating diagram: " + err.message);
        } finally {
          setIsLoading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (e: any) {
      alert("Error reading file: " + e.message);
      setIsLoading(false);
    }
    
    e.target.value = '';
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
        document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen]);

  useEffect(() => {
    const interval = setInterval(() => {
      const node = document.getElementById('ai-toolbox-portal-target');
      if (node) {
        setToolbarNode(node as HTMLElement);
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const getGeminiModel = async () => {
    const { GoogleGenerativeAI } = await import("@google/generative-ai");
    const apiKey = import.meta.env.VITE_APP_GEMINI_API_KEY;
    if (!apiKey) throw new Error("Missing VITE_APP_GEMINI_API_KEY");
    const genAI = new GoogleGenerativeAI(apiKey);
    return genAI.getGenerativeModel({ model: "gemini-2.5-flash" });
  };

  const getCanvasImage = async () => {
    const elements = excalidrawAPI.getSceneElements();
    const selected = elements.filter(el => excalidrawAPI.getAppState().selectedElementIds[el.id]);
    const targetElements = selected.length > 0 ? selected : elements;
    
    if (targetElements.length === 0) throw new Error("Canvas is empty");

    const appState = excalidrawAPI.getAppState();
    const blob = await exportToBlob({
      elements: targetElements,
      appState: {
        ...appState,
        exportBackground: true,
        viewBackgroundColor: appState.viewBackgroundColor,
      },
      files: excalidrawAPI.getFiles(),
      mimeType: MIME_TYPES.jpg,
    });
    return getDataURL(blob);
  };

  const handleAction = async (action: string) => {
    try {
      setIsLoading(true);
      const model = await getGeminiModel();
      let prompt = "";
      let imagePart: any = null;

      if (action === "mindmap" || action === "code_to_tree") {
         let topic = "";
         if (action === "mindmap") {
             topic = window.prompt("Enter the main topic for the mind map:") || "";
         } else {
             topic = window.prompt("Paste your Flutter/Dart code here:") || "";
         }
         
         if (!topic) {
             setIsLoading(false);
             return;
         }
         
         if (action === "mindmap") {
             prompt = `Generate a Mermaid JS mindmap or flowchart about: ${topic}. Output ONLY valid Mermaid syntax inside \`\`\`mermaid ... \`\`\` block. CRITICAL: You MUST ALWAYS start your output with a valid diagram type declaration (e.g., \`flowchart TD\`). NEVER start directly with a \`subgraph\`.`;
         } else {
             prompt = `Analyze the following Flutter/Dart code and generate a Mermaid JS flowchart or tree diagram illustrating the Widget Tree or Architecture. Output ONLY valid Mermaid syntax inside \`\`\`mermaid ... \`\`\` block.\n\nCRITICAL: You MUST ALWAYS start your output with a valid diagram type declaration (e.g., \`flowchart TD\`, \`graph LR\`). NEVER start directly with a \`subgraph\`.\n\nCode:\n${topic}`;
         }
      } else {
         const dataURL = await getCanvasImage();
         const base64Data = dataURL.split(",")[1];
         const mimeType = dataURL.split(",")[0].split(":")[1].split(";")[0];
         imagePart = { inlineData: { data: base64Data, mimeType } };
         
         const text = getTextFromElements(excalidrawAPI.getSceneElements());

         if (action === "summarize") {
            prompt = `Summarize the architecture or flow shown in this diagram. Be concise and professional. Additional text context: ${text}`;
         } else if (action === "iac") {
            prompt = `Based on the provided architecture diagram, generate a Terraform script to provision these resources. Output ONLY valid HCL code inside \`\`\`hcl ... \`\`\` block. Additional text context: ${text}`;
         } else if (action === "flutter_code") {
            prompt = `You are an expert Flutter Developer. Look at this wireframe/UI design diagram and generate the complete Flutter Dart code (Stateless or StatefulWidget) to build it. Use Material or Cupertino components, standard layout widgets (Column, Row, Container, etc). Output ONLY valid Dart code inside \`\`\`dart ... \`\`\` block. Context text: ${text}`;
         } else if (action === "state_mgmt") {
            prompt = `You are an expert Flutter Architect. Based on this state management or data flow diagram, generate the boilerplate code for the state management (e.g. BLoC, Riverpod, or Provider). Output ONLY valid Dart code inside \`\`\`dart ... \`\`\` block. Context text: ${text}`;
         } else if (action === "dart_models") {
            prompt = `You are an expert Dart Developer. Based on this ER diagram or database schema drawn in the image, generate the corresponding Dart Data Model classes. Include fromJson, toJson, and copyWith methods, or use Freezed annotations. Output ONLY valid Dart code inside \`\`\`dart ... \`\`\` block. Context text: ${text}`;
         } else if (action === "api_service") {
            prompt = `You are an expert Flutter Developer. Based on this API flow or backend diagram, generate a Dart HTTP or Dio API Service class with robust error handling. Output ONLY valid Dart code inside \`\`\`dart ... \`\`\` block. Context text: ${text}`;
         }
      }

      const parts = imagePart ? [prompt, imagePart] : [prompt];
      const result = await model.generateContent(parts);
      const responseText = await result.response.text();
      const tokensUsed = result.response.usageMetadata?.totalTokenCount || 0;
      trackAIUsage(tokensUsed);

      let cleanText = responseText;
      let lang = "text";
      if (action === "mindmap" || action === "code_to_tree") lang = "mermaid";
      if (action === "iac") lang = "hcl";
      if (["flutter_code", "state_mgmt", "dart_models", "api_service"].includes(action)) lang = "dart";

      const match = responseText.match(/```(\w+)([\s\S]*?)```/);
      if (match) {
        lang = match[1] || lang;
        cleanText = match[2].trim();
      } else if (responseText.startsWith("```") && responseText.endsWith("```")) {
        cleanText = responseText.substring(3, responseText.length - 3).trim();
      }

      let title = "Diagram Summary";
      if (action === "iac") title = "Terraform IaC";
      if (action === "mindmap") title = "Mermaid Mindmap";
      if (action === "flutter_code") title = "Flutter Code (UI)";
      if (action === "state_mgmt") title = "State Management Code";
      if (action === "dart_models") title = "Dart Models";
      if (action === "api_service") title = "Flutter API Service";
      if (action === "code_to_tree") title = "Widget Tree Diagram";

      setModalContent({
        title,
        content: cleanText,
        language: lang
      });

    } catch (e: any) {
      alert("Error: " + e.message);
    } finally {
      setIsLoading(false);
    }
  };

  const triggerBtnStyle = {
    padding: '0 8px',
    height: '36px',
    borderRadius: '6px',
    border: 'none',
    background: isDropdownOpen ? 'var(--color-primary-light, #e0eaff)' : 'transparent',
    color: isDropdownOpen ? 'var(--color-primary, #3b82f6)' : 'var(--icon-fill-color, #1e1e1e)',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const modalBtnStyle = {
    padding: '0 8px',
    height: '36px',
    borderRadius: '6px',
    border: 'none',
    background: 'var(--color-primary-light, #e0eaff)',
    color: 'var(--color-primary, #3b82f6)',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const modalOverlayStyle = {
    position: 'fixed' as const,
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100000,
  };

  const modalStyle = {
    background: 'var(--island-bg-color, white)',
    padding: '20px',
    borderRadius: '8px',
    width: '600px',
    maxWidth: '90%',
    maxHeight: '80%',
    display: 'flex',
    flexDirection: 'column' as const,
    boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
    color: 'var(--text-primary-color)'
  };

  const toolboxButtons = (
    <div ref={dropdownRef} style={{ display: 'flex', alignItems: 'center', marginLeft: '8px', position: 'relative' }}>
      <style>{`
        .ai-dropdown-item {
          padding: 10px 16px;
          border: none;
          background: transparent;
          color: var(--text-primary-color);
          cursor: pointer;
          text-align: left;
          width: 100%;
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 14px;
        }
        .ai-dropdown-item:hover {
          background: var(--color-gray-10, #f3f4f6);
        }
        .ai-dropdown-item:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
      <div className="App-toolbar__divider" />
      <button 
        style={triggerBtnStyle} 
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        title="AI Tools"
      >
        ✨
      </button>

      {isDropdownOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          marginTop: '8px',
          background: 'var(--island-bg-color, white)',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          border: '1px solid var(--color-gray-20)',
          minWidth: '220px',
          display: 'flex',
          flexDirection: 'column',
          padding: '8px 0',
          zIndex: 999999
        }}>
           <div style={{ padding: '4px 16px', fontSize: '11px', fontWeight: 'bold', color: 'var(--color-gray-50)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>General</div>
           <button className="ai-dropdown-item" disabled={isLoading} onClick={() => fileInputRef.current?.click()}>🖼️ Image to Diagram</button>
           <button className="ai-dropdown-item" disabled={isLoading} onClick={() => { setIsDropdownOpen(false); handleAction("summarize"); }}>📝 Summarize Diagram</button>
           <button className="ai-dropdown-item" disabled={isLoading} onClick={() => { setIsDropdownOpen(false); handleAction("iac"); }}>☁️ Generate IaC</button>
           <button className="ai-dropdown-item" disabled={isLoading} onClick={() => { setIsDropdownOpen(false); handleAction("mindmap"); }}>🧠 Mind Map</button>
           
           <div style={{ margin: '8px 0', borderTop: '1px solid var(--color-gray-20)' }} />
           
           <div style={{ padding: '4px 16px', fontSize: '11px', fontWeight: 'bold', color: 'var(--color-gray-50)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Flutter & Dart</div>
           <button className="ai-dropdown-item" disabled={isLoading} onClick={() => { setIsDropdownOpen(false); handleAction("flutter_code"); }}>📱 Wireframe to Code</button>
           <button className="ai-dropdown-item" disabled={isLoading} onClick={() => { setIsDropdownOpen(false); handleAction("state_mgmt"); }}>🔄 Gen State Management</button>
           <button className="ai-dropdown-item" disabled={isLoading} onClick={() => { setIsDropdownOpen(false); handleAction("dart_models"); }}>🗃️ Gen Dart Models</button>
           <button className="ai-dropdown-item" disabled={isLoading} onClick={() => { setIsDropdownOpen(false); handleAction("api_service"); }}>🌐 Gen API Service</button>
           <button className="ai-dropdown-item" disabled={isLoading} onClick={() => { setIsDropdownOpen(false); handleAction("code_to_tree"); }}>🌳 Code to Widget Tree</button>
        </div>
      )}
    </div>
  );

  return (
    <>
      <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" onChange={handleImageUpload} />
      {toolbarNode ? createPortal(toolboxButtons, toolbarNode) : null}

      {modalContent && (
        <div style={modalOverlayStyle}>
          <div style={modalStyle}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
              <h3 style={{margin: 0}}>{modalContent.title}</h3>
              <button onClick={() => setModalContent(null)} style={{background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: 'var(--text-primary-color)'}}>&times;</button>
            </div>
            <pre style={{
              background: 'var(--color-gray-10)',
              padding: '15px',
              borderRadius: '6px',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              color: 'var(--text-primary-color)',
              flex: 1,
              margin: 0
            }}>
              <code>{modalContent.content}</code>
            </pre>
            <div style={{display: 'flex', gap: '10px', marginTop: '15px', flexShrink: 0}}>
               {modalContent.language === "mermaid" && (
                  <button onClick={async () => {
                    try {
                      const { parseMermaidToExcalidraw } = await import("@excalidraw/mermaid-to-excalidraw");
                      const { elements } = await parseMermaidToExcalidraw(modalContent.content);
                      
                      const currentElements = excalidrawAPI.getSceneElements();
                      excalidrawAPI.updateScene({ elements: [...currentElements, ...elements] });
                      setModalContent(null);
                    } catch (e: any) {
                      alert("Failed to parse Mermaid code. Check if the generated syntax is valid. Error: " + e.message);
                    }
                  }} style={{...modalBtnStyle, background: 'var(--color-success, #10b981)', color: 'white', flex: 1}}>
                    Insert to Canvas
                  </button>
               )}
               <button onClick={() => {
                 navigator.clipboard.writeText(modalContent.content);
                 alert("Copied to clipboard!");
               }} style={{...modalBtnStyle, flex: 1}}>Copy to Clipboard</button>
               <button onClick={() => setModalContent(null)} style={{...modalBtnStyle, background: 'var(--color-gray-40, #9ca3af)', color: 'white', flex: 1}}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export const AIComponents = ({
  excalidrawAPI,
}: {
  excalidrawAPI: ExcalidrawImperativeAPI;
}) => {
  return (
    <>
      <AIToolbox excalidrawAPI={excalidrawAPI} />
      <DiagramToCodePlugin
        generate={async ({ frame, children }) => {
          const appState = excalidrawAPI.getAppState();

          const blob = await exportToBlob({
            elements: children,
            appState: {
              ...appState,
              exportBackground: true,
              viewBackgroundColor: appState.viewBackgroundColor,
            },
            exportingFrame: frame,
            files: excalidrawAPI.getFiles(),
            mimeType: MIME_TYPES.jpg,
          });

          const dataURL = await getDataURL(blob);
          const textFromFrameChildren = getTextFromElements(children);

          try {
            // Import Gemini SDK dynamically or statically
            const { GoogleGenerativeAI } = await import("@google/generative-ai");
            const apiKey = import.meta.env.VITE_APP_GEMINI_API_KEY;
            
            if (!apiKey) {
              throw new Error("Missing VITE_APP_GEMINI_API_KEY in .env.development");
            }

            const genAI = new GoogleGenerativeAI(apiKey);
            const model = genAI.getGenerativeModel({ model: "gemini-2.5-flash" });

            const base64Data = dataURL.split(",")[1];
            const mimeType = dataURL.split(",")[0].split(":")[1].split(";")[0];
            const imagePart = {
              inlineData: {
                data: base64Data,
                mimeType
              },
            };

            const prompt = `You are an expert UI/UX designer and frontend developer. Transform the provided wireframe into a breathtaking, production-ready Modern UI. 
Requirements:
1. Use a single HTML file with Tailwind CSS via CDN.
2. Apply a premium modern design system (e.g., glassmorphism, soft shadows, vibrant gradients, or a sleek dark mode).
3. Use modern typography (e.g., Google Fonts like 'Inter' or 'Outfit' via CDN).
4. Add micro-animations (hover effects, smooth transitions).
5. Ensure elements look polished, rounded, and highly professional. Do not just make a basic layout.
Output ONLY valid HTML inside \`\`\`html ... \`\`\` block. No conversational text.
Additional context from text in the wireframe: ${textFromFrameChildren}`;

            const result = await model.generateContent([prompt, imagePart]);
            const response = await result.response;
            const text = response.text();
            const tokensUsed = response.usageMetadata?.totalTokenCount || 0;
            trackAIUsage(tokensUsed);

            let html = text;
            const match = text.match(/```html([\s\S]*?)```/);
            if (match) {
              html = match[1].trim();
            } else if (text.startsWith("```") && text.endsWith("```")) {
              html = text.substring(3, text.length - 3).trim();
            }

            if (!html) {
              throw new Error("Generation failed (empty response from Gemini)");
            }

            return { html };
          } catch (error: any) {
            console.error("Gemini Generation Error:", error);
            throw new Error(error.message || "Generation failed");
          }
        }}
      />

      <TTDDialog
        persistenceAdapter={ttdPersistenceAdapter}
        onTextSubmit={async ({ messages, onChunk }) => {
          try {
            const { GoogleGenerativeAI } = await import("@google/generative-ai");
            const apiKey = import.meta.env.VITE_APP_GEMINI_API_KEY;
            
            if (!apiKey) {
              throw new Error("Missing VITE_APP_GEMINI_API_KEY in .env.development");
            }

            const genAI = new GoogleGenerativeAI(apiKey);
            const model = genAI.getGenerativeModel({ model: "gemini-2.5-flash" });

            const history = messages.slice(0, -1).map(msg => ({
              role: msg.role === "assistant" ? "model" : "user",
              parts: [{ text: msg.content }],
            }));
            const latestMessage = messages[messages.length - 1].content;

            const chat = model.startChat({ history });

            const prompt = `You are an expert at creating Mermaid JS diagrams for modern application architectures and workflows.
Respond ONLY with valid Mermaid JS code. Do not wrap it in markdown code blocks (\`\`\`mermaid ... \`\`\`), just return the raw Mermaid syntax. Do not add any conversational text before or after.
CRITICAL: You MUST ALWAYS start your output with a valid diagram type declaration (e.g., \`flowchart TD\`, \`graph LR\`, \`sequenceDiagram\`). NEVER start directly with a \`subgraph\`.
IMPORTANT: Do NOT use \`classDef\`, \`class\`, or any custom styling commands. The parser only supports basic nodes and edges.
To make the diagrams look extraordinary and modern:
1. Use \`subgraph\` to logically group components (e.g., Frontend, Backend, Database, User Roles).
2. Create highly detailed and comprehensive flows.
3. Use descriptive, professional labels for all nodes and edges.
4. Maintain a clean, logical layout (usually \`graph TD\` or \`graph LR\`).
User request: ${latestMessage}`;

            const result = await chat.sendMessage(prompt);
            const response = await result.response;
            let text = response.text();

            // Clean up if Gemini still wrapped it in markdown
            let cleanText = text;
            const match = text.match(/```mermaid([\s\S]*?)```/);
            if (match) {
              cleanText = match[1].trim();
            } else if (text.startsWith("```") && text.endsWith("```")) {
              cleanText = text.substring(3, text.length - 3).trim();
            }

            if (onChunk) {
              onChunk(cleanText);
            }

            return { generatedResponse: cleanText, error: null };
          } catch (error: any) {
            console.error("Gemini TTD Error:", error);
            return {
              error: { message: error.message || "Generation failed" } as any,
            };
          }
        }}
      />
    </>
  );
};
